import type { ReactNode } from 'react';
import { Boton } from './Boton';
import { Icono } from './Icono';
import type { NombreIcono } from './iconos';

/**
 * Estados comunes de una página (0.4, componentes.md §14), dentro del contenido del marco y en tarjeta blanca centrada:
 * cargando, vacía, error con código para soporte, sin permiso (sin «Pedir acceso», P0-6) y bloque no contratado. Textos:
 * los del prototipo 0.1 con el nombre de la sección. El «solo lectura» no es una página: chip y franja (ver Marco).
 */
interface PropsTarjeta {
    icono: NombreIcono;
    baldosa: string;
    tinta: string;
    ceja: string;
    cejaColor: string;
    titulo: string;
    texto: string;
    lista?: readonly string[];
    botones?: ReactNode;
    codigo?: string | null;
}

export function Tarjeta({ icono, baldosa, tinta, ceja, cejaColor, titulo, texto, lista, botones, codigo }: PropsTarjeta) {
    return (
        <div className="rv-estado">
            <span className="rv-estado__baldosa" style={{ background: baldosa, color: tinta }}>
                <Icono nombre={icono} />
            </span>
            <div className="rv-estado__ceja" style={{ color: cejaColor }}>
                {ceja}
            </div>
            <div className="rv-estado__titulo">{titulo}</div>
            <div className="rv-estado__texto">{texto}</div>
            {lista !== undefined && lista.length > 0 && (
                <div className="rv-estado__lista">
                    {lista.map((t, i) => (
                        <div key={t} style={{ animation: `rvRise 420ms cubic-bezier(0.16,1,0.3,1) ${String(240 + i * 50)}ms both` }}>
                            <Icono nombre="check" />
                            <span>{t}</span>
                        </div>
                    ))}
                </div>
            )}
            {botones !== undefined && <div className="rv-estado__botones">{botones}</div>}
            {codigo != null && <div className="rv-estado__codigo">{`Código para soporte: ${codigo}`}</div>}
        </div>
    );
}

/** Cargando: barra roja fina que recorre el ancho, bloques con brillo y «Cargando inicio…». */
export function EstadoCargando({ seccion }: { seccion: string }) {
    const anchos = ['54%', '42%', '61%', '47%', '35%'];
    return (
        <div className="rv-cargando" role="status" aria-live="polite">
            <div className="rv-cargando__barra">
                <span />
            </div>
            <div className="rv-cargando__cifras">
                {[0, 1, 2].map((i) => (
                    <div key={i} className="rv-brillo" style={{ animation: `rvRise 420ms cubic-bezier(0.16,1,0.3,1) ${String(i * 60)}ms both, rvShimmer 1.8s linear infinite` }} />
                ))}
            </div>
            <div className="rv-cargando__lista">
                {anchos.map((w, i) => (
                    <div key={w} className="rv-cargando__fila" style={{ animation: `rvRise 420ms cubic-bezier(0.16,1,0.3,1) ${String(120 + i * 60)}ms both` }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <span className="rv-brillo" style={{ height: 12, width: w, borderRadius: 6 }} />
                            <span className="rv-brillo" style={{ height: 10, width: '30%', borderRadius: 6 }} />
                        </div>
                        <span className="rv-brillo" style={{ height: 24, width: 96, borderRadius: 999 }} />
                    </div>
                ))}
            </div>
            <div className="rv-cargando__texto">{`Cargando ${seccion.toLocaleLowerCase('es')}…`}</div>
        </div>
    );
}

export function EstadoVacia({ seccion, accion }: { seccion: string; accion?: ReactNode }) {
    return (
        <Tarjeta
            icono="inbox"
            baldosa="var(--rv-bone)"
            tinta="var(--rv-fg-2)"
            ceja="Vacía"
            cejaColor="var(--rv-fg-3)"
            titulo={`Todavía no hay nada en ${seccion}`}
            texto="Cuando llegue lo primero, lo verás aquí."
            botones={accion}
        />
    );
}

export function EstadoError({ seccion, codigoSoporte, onReintentar, movil = false }: { seccion: string; codigoSoporte: string | null; onReintentar: () => void; movil?: boolean }) {
    return (
        <Tarjeta
            icono="triangle-alert"
            baldosa="var(--rv-red-soft)"
            tinta="var(--rv-red)"
            ceja="Error"
            cejaColor="var(--rv-red)"
            titulo={`No he podido cargar ${seccion}`}
            texto="Puede ser la conexión. Tus datos están a salvo. Prueba de nuevo."
            botones={
                <Boton icono="refresh-cw" gesto="gira" movil={movil} onClick={onReintentar}>
                    Reintentar
                </Boton>
            }
            codigo={codigoSoporte}
        />
    );
}

/** Sin permiso (P0-6, opción a): qué no puede ver, con qué rol entra y a quién pedírselo; «Ir a Inicio». Sin «Pedir acceso». */
export function EstadoSinPermiso({ seccion, rol, negocio, onInicio, movil = false }: { seccion: string; rol: string; negocio: string; onInicio: (() => void) | null; movil?: boolean }) {
    return (
        <Tarjeta
            icono="lock"
            baldosa="var(--rv-ink)"
            tinta="#fff"
            ceja="Sin permiso"
            cejaColor="var(--rv-fg-3)"
            titulo={`Tu rol no puede ver ${seccion}`}
            texto={`Entras como ${rol} en ${negocio}. Pídeselo al propietario o a un administrador.`}
            botones={
                onInicio === null ? undefined : (
                    <Boton tipo="secundario" icono="house" movil={movil} onClick={onInicio}>
                        Ir a Inicio
                    </Boton>
                )
            }
        />
    );
}

/**
 * Bloque no contratado: qué añade el bloque (nombre, descripción y tres ventajas, que saldrán del catálogo, P0-7) y, al
 * Propietario, «Añadir a mi plan» y «Hablar con ventas» (sin destino hasta P0-7). Quien no es Propietario no ve la
 * entrada en el menú (G120); si llega por un enlace, «Ir a Inicio».
 */
export function EstadoNoContratado({
    bloque,
    texto,
    incluye,
    propietario,
    onAnadir,
    onInicio,
    movil = false,
}: {
    bloque: string;
    /** El texto propio del bloque cuando su dibujo lo trae (10.1, 10.9); sin él, el de 0.4. */
    texto?: string;
    incluye: readonly string[];
    propietario: boolean;
    onAnadir: (() => void) | null;
    onInicio: (() => void) | null;
    movil?: boolean;
}) {
    return (
        <Tarjeta
            icono="puzzle"
            baldosa="var(--rv-red)"
            tinta="#fff"
            ceja="No contratado"
            cejaColor="var(--rv-red)"
            titulo={bloque}
            texto={
                texto ?? (propietario
                    ? 'Este bloque no está en tu plan. Lo añades en un momento y lo quitas cuando quieras.'
                    : 'Este bloque no está en el plan del negocio. Solo el propietario puede añadirlo. Pídeselo al propietario.')
            }
            lista={incluye}
            botones={
                propietario ? (
                    <>
                        <Boton icono="plus" movil={movil} disabled={onAnadir === null} onClick={onAnadir ?? undefined}>
                            Añadir a mi plan
                        </Boton>
                        {/* «Hablar con ventas» sin destino: espera P0-7. */}
                        <Boton tipo="secundario" icono="message-circle" movil={movil} aria-disabled="true">
                            Hablar con ventas
                        </Boton>
                    </>
                ) : onInicio === null ? undefined : (
                    <Boton tipo="secundario" icono="house" movil={movil} onClick={onInicio}>
                        Ir a Inicio
                    </Boton>
                )
            }
        />
    );
}

/** Hueco de una pantalla que todavía no se ha programado: se hace con su módulo (G61, G127). */
export function Hueco({ seccion, icono }: { seccion: string; icono: NombreIcono }) {
    return (
        <div className="rv-hueco">
            <Icono nombre={icono} />
            <strong>{`Contenido de ${seccion}`}</strong>
            <span>Se diseña en su módulo.</span>
        </div>
    );
}
